/**
 * resolveReminder — ONE rule for what time a reminder fires at.
 *
 * WHY (Walter 2026-09-10, proposal docs/proposals/2026-09-10-one-shape-for-a-reminder.md).
 * He created two reminders through the agenda's "+ new" popover with no time
 * and got "tomorrow 5:34pm" — the clock time when he typed them. The same
 * words typed into a list give 7am. Four routes create or edit reminders and
 * each re-derived the time on its own:
 *
 *   1. typing a line + Enter  — default time from his setting            ✅
 *   2. the reconciler         — default time, but a hard-coded 7am,
 *                               ignoring his setting                     ⚠️
 *   3. popover "+ new"        — chrono's raw clock time, no default      ❌
 *   4. popover edit           — the same raw clock time                  ❌
 *
 * The parser always knew whether a time was typed (`hasCertainTime`); the
 * popover threw that away. This module is the single decision all four use.
 *
 * The rule, in order:
 *   a. a time the user TYPED always wins ("tomorrow 3pm" is 3pm) — Walter's
 *      answer "i yes", 2026-09-14;
 *   b. no typed time, editing a reminder that already has one → keep its
 *      time-of-day (2026-08-24: editing "9/1 9am" down to "9/6" must not
 *      silently move it to 7am — "never a surprise 7am");
 *   c. otherwise → his configured default (Settings → default reminder time).
 *
 * Callers that pass an explicit, already-computed Date (snooze, the date
 * buttons) simply omit `hasCertainTime` — an omitted flag means "certain", so
 * those paths are untouched.
 */

export const FALLBACK_DEFAULT_TIME = Object.freeze({ hour: 7, minute: 0 });

/**
 * parseDefaultTime("HH:MM") → { hour, minute }. Missing or malformed falls
 * back to 7:00, clamped to a valid clock. Same rules the capture hook used.
 */
export function parseDefaultTime(value) {
  if (value && typeof value === 'object'
    && Number.isFinite(value.hour) && Number.isFinite(value.minute)) {
    return {
      hour: Math.max(0, Math.min(23, Math.trunc(value.hour))),
      minute: Math.max(0, Math.min(59, Math.trunc(value.minute))),
    };
  }
  const m = String(value ?? '').match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return { ...FALLBACK_DEFAULT_TIME };
  return {
    hour: Math.max(0, Math.min(23, parseInt(m[1], 10))),
    minute: Math.max(0, Math.min(59, parseInt(m[2], 10))),
  };
}

/** A Date from a Date, a Firestore Timestamp, a millis number or a date string. */
function toValidDate(v) {
  if (v == null) return null;
  const d = typeof v?.toDate === 'function' ? v.toDate() : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** { hour, minute } of an existing reminder's date, or null. */
export function timeOfDay(v) {
  const d = toValidDate(v);
  return d ? { hour: d.getHours(), minute: d.getMinutes() } : null;
}

/**
 * resolveReminderDate({ parsedDate, hasCertainTime, defaultTime, existingDate })
 * → Date | null
 *
 *   parsedDate     — the date as parsed (chrono fills in the CURRENT clock
 *                    time when none was typed — the 5:34pm)
 *   hasCertainTime — false when no time was typed; omit for explicit Dates
 *   defaultTime    — "HH:MM" string or { hour, minute }; defaults to 7:00
 *   existingDate   — the reminder's current date when editing, else omit
 *
 * Always returns a NEW Date; never mutates its input.
 */
export function resolveReminderDate({ parsedDate, hasCertainTime, defaultTime, existingDate } = {}) {
  const base = toValidDate(parsedDate);
  if (!base) return null;
  // (a) a typed time wins — and an omitted flag means the caller computed
  // the exact Date itself, so leave it alone.
  if (hasCertainTime !== false) return new Date(base.getTime());
  const out = new Date(base.getTime());
  // (b) keep the time the reminder already had.
  const keep = timeOfDay(existingDate);
  if (keep) {
    out.setHours(keep.hour, keep.minute, 0, 0);
    return out;
  }
  // (c) his default.
  const { hour, minute } = parseDefaultTime(defaultTime);
  out.setHours(hour, minute, 0, 0);
  return out;
}
